import { ShoppingCartRounded } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { usePlaceTestCharityOrders } from "@/hooks/useCampaignTester";
import type { CampaignDonation } from "@/interfaces/campaignTester";
import { formatDate } from "@/utils/date-format";

/** CharityOrderStatus — mirrors the API constant. */
const ORDER_STATUS: Record<
  number,
  { label: string; colour: "default" | "info" | "success" | "error" }
> = {
  0: { label: "Pending order", colour: "default" },
  1: { label: "Order created", colour: "info" },
  2: { label: "Fulfilled", colour: "success" },
  3: { label: "Failed", colour: "error" },
};

const th = {
  fontSize: 10.5,
  letterSpacing: "0.6px",
  textTransform: "uppercase" as const,
  color: "var(--color-graphite)",
  fontWeight: 700,
  whiteSpace: "nowrap" as const,
};

/**
 * Where a closed campaign's unspent credits went.
 *
 * Only rendered once a donation exists — before close there is nothing to show, and a panel reading
 * "0 donated" on a live campaign implies a decision that hasn't been made yet.
 *
 * The amounts are the SNAPSHOTS the donation wrote, not a recalculation from today's prices: the
 * charity catalogue is repriced from time to time, and a receipt that changes after the fact is not
 * a receipt. `units` is derived for readability and is the only figure here that can drift.
 */
export default function DonationPanel({
  donation,
  campaignId,
}: {
  donation: CampaignDonation;
  campaignId: number;
}) {
  const total = Number(donation.credits);
  const place = usePlaceTestCharityOrders();

  // Only lines still waiting are worth a button. Once every one carries an order id there is
  // nothing left to buy, and offering it again invites a second click that would do nothing.
  const pending = donation.lines.filter((l) => l.shopifyOrderId == null).length;

  return (
    <Box
      sx={{
        border: "1px solid var(--color-card-border)",
        borderRadius: 2,
        bgcolor: "#fff",
        mb: 2,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        sx={{ px: 2, pt: 1.6, pb: 1.2 }}
      >
        <Box>
          <Typography sx={{ fontFamily: "var(--font-crimson)", fontSize: 17 }}>
            Donated at close
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "var(--color-graphite)" }}>
            Unredeemed credits spent on the campaign's causes — {formatDate(donation.occurredAt)}.
            Consumption event #{donation.consumptionEventId}.
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {pending > 0 && (
            <Button
              size="small"
              variant="outlined"
              startIcon={
                place.isPending ? (
                  <CircularProgress aria-label="Loading" size={13} />
                ) : (
                  <ShoppingCartRounded />
                )
              }
              disabled={place.isPending}
              onClick={() => place.mutate(campaignId)}
              sx={{ whiteSpace: "nowrap" }}
            >
              {place.isPending ? "Placing…" : `Place ${pending} order${pending === 1 ? "" : "s"}`}
            </Button>
          )}
          <Typography sx={{ fontFamily: "var(--font-crimson)", fontSize: 21 }}>
            {total.toLocaleString()}
          </Typography>
        </Stack>
      </Stack>

      {/* Said once, where the button is. A seeded campaign is simulated everywhere else in this
          screen; this is the one control that spends real money at the configured store. */}
      {pending > 0 && (
        <Typography sx={{ px: 2, pb: 1, fontSize: 11, color: "var(--color-graphite)" }}>
          Places real paid orders in Shopify — one per cause, tagged to this donation so a repeat
          click cannot buy twice.
        </Typography>
      )}
      {place.isError && (
        <Alert severity="error" sx={{ mx: 2, mb: 1.5 }}>
          {place.error instanceof Error ? place.error.message : "The orders could not be placed."}
        </Alert>
      )}

      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={th}>Cause</TableCell>
              <TableCell sx={th} align="right">
                Units
              </TableCell>
              <TableCell sx={th} align="right">
                Credits
              </TableCell>
              <TableCell sx={th} align="right">
                Share
              </TableCell>
              <TableCell sx={th}>Charity order</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {donation.lines.map((l) => {
              const status = ORDER_STATUS[l.status] ?? ORDER_STATUS[0]!;
              return (
                <TableRow key={l.causeProductId}>
                  <TableCell sx={{ fontSize: 12.5 }}>
                    {l.title}
                    {l.price != null && (
                      <Typography
                        component="span"
                        sx={{ fontSize: 11, color: "var(--color-graphite)", ml: 0.8 }}
                      >
                        @ {l.price}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12.5 }}>
                    {l.units ?? "—"}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12.5 }}>
                    {Number(l.amount).toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12.5 }}>
                    {(Number(l.share) * 100).toFixed(1)}%
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={status.label}
                        color={status.colour}
                        sx={{ fontSize: 10.5 }}
                      />
                      {l.shopifyOrderId && (
                        <Typography sx={{ fontSize: 11, color: "var(--color-graphite)" }}>
                          {l.shopifyOrderId}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Typography sx={{ fontSize: 11, color: "var(--color-good-night)", px: 2, py: 1.2 }}>
        The credits were drawn FIFO from the group's lots at close and posted DR deferred revenue /
        CR pending fulfilment. Revenue is recognised per cause when its charity order ships — which
        is what the status column is waiting on.
      </Typography>
    </Box>
  );
}
